import { useState } from "react";
import * as React from "react";
import { useRoute } from "wouter";
import { ArrowLeft, RemoveFormatting } from "lucide-react";
import {
  getVersionForm,
} from "@/hooks/useLocalStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationControl } from "@/widgets/renderPagination";
import { Popup } from "@/widgets/RuleDetailDailog";
import { PortalForm } from "@/lib/api-services";

interface TableHierarchicalNodeProps {
  topLevelTypesData: PortalForm[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (code: number) => void;
}

function TableRule({
  topLevelTypesData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<PortalForm[]>([]);
  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<PortalForm | undefined>();
  const [dataContent, setDataContent] = React.useState<string | null>("");
  const [popupName, setPopupName] = React.useState("");

  React.useEffect(() => {
    if (topLevelTypesData) {
      setCurrentData(topLevelTypesData);
    }
  }, [topLevelTypesData]);

  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <>
                <TableHead>ID</TableHead>
                <TableHead>Form ID</TableHead>
                <TableHead>Form Code</TableHead>
                <TableHead>Form Name</TableHead>
                <TableHead>Partner Type</TableHead>
                <TableHead>Form Type</TableHead>
                <TableHead>Build Form</TableHead>
                <TableHead>Version No</TableHead>
              </>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.Id}>
                <TableCell className="font-mono">{data.Id}</TableCell>
                <TableCell className="font-mono">{data.FormId}</TableCell>
                <TableCell className="font-mono">{data.FormCode}</TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.FormName}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.FormPartnerName}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.FormType}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Raw with Value")
                    }}
                  >
                    <RemoveFormatting />
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Version}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>

        {showPopup && (
          <Popup
            popupName={popupName}
            heading={dataHead}
            content={dataContent}
            onClose={() => setShowPopup(false)}
          />
        )}
      </div>
    </div>


  );
}

export function RuleVersionHistory() {

  const [, params] = useRoute("/ruleversion/:ruleId/:name");
  const ruleId = params?.ruleId;
  const formName = params?.name;
  const [topLevelTypes, setTopLevelTypes] = useState<PortalForm[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(10);
  const { data: versions, isLoading } = getVersionForm(
    Number(ruleId),
    formName || "",
    pageIndex,
    pageSize
  );

  React.useEffect(() => {
    setTopLevelTypes(versions?.items ?? []);
    setTotalCount(versions?.meta?.total ?? 0);
  }, [versions]);

  // Check for navigation from search
  React.useEffect(() => {
    const selectedItemStr = localStorage.getItem("cm:selectedItem");
    if (selectedItemStr) {
      try {
        const selectedItem = JSON.parse(selectedItemStr);
        console.log("Navigate to item in TreeExplorer:", selectedItem);
        // Clear the selection after handling
        localStorage.removeItem("cm:selectedItem");

        // TODO: Expand tree to show the selected item
        // This could involve expanding parent nodes and highlighting the target
      } catch (error) {
        console.error("Error parsing selected item:", error);
        localStorage.removeItem("cm:selectedItem");
      }
    }
  }, []);


  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Version History
            </h2>
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              Manage and review the version history of your Forms.
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
        </div>
      </div>

      {!isLoading ? (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-2">
              {topLevelTypes && topLevelTypes.length > 0 ? (
                <>
                  <TableRule
                    topLevelTypesData={topLevelTypes}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={setPageIndex}
                  />
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No Version found. Create to get started.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-6">Loading...</div>
      )}
    </div>
  );
}
